import Link from 'next/link';
import { BsBox } from 'react-icons/bs';

import {
    Anchor,
    AnchorProps,
    Box,
    Button,
    ButtonProps,
    Card,
    Flex,
    Group,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from '@mantine/core';
import { IoAdd } from 'node_modules/react-icons/io5';

import { CompanyProfile } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { MarketingHelpers } from 'helpers/MarketingHelpers';

import { useCurrentTeamCompanies } from 'hooks/use-current-team-companies';
import { useCurrentUser } from 'hooks/use-current-user';
import { ModalService } from 'services/ModalService';

import cx from './AddProductCard.module.scss';

type AddProductAction = { explainsPro: boolean; href?: string; onClick?: () => void };

const useAddProductAction = (): AddProductAction => {
    const user = useCurrentUser();
    const { companies } = useCurrentTeamCompanies();

    let company: CompanyProfile | undefined;

    if (companies.length === 1) {
        company = companies[0];
    }

    if (user?.hasCompany) {
        return { explainsPro: false, href: ComponentHelpers.urls.create({ company }) };
    }

    const profileSignupUrl = CompanyHelpers.urls.create({ redirect: ComponentHelpers.urls.create() });

    if (user) {
        return { explainsPro: true, href: profileSignupUrl };
    }

    return {
        explainsPro: true,
        onClick: () =>
            ModalService.openLoginModal({
                redirect: profileSignupUrl,
                title: (
                    <>
                        Want to add a product? <br />
                        Create an account first
                    </>
                ),
                message: 'Existing users will receive a login link via email.',
            }),
    };
};

const AddProductButton = ({ action, ...props }: { action: AddProductAction } & ButtonProps) =>
    action.href ? (
        <Button component={Link} href={action.href} leftSection={<IoAdd />} {...props}>
            Add Your Product
        </Button>
    ) : (
        <Button type="button" onClick={action.onClick} leftSection={<IoAdd />} {...props}>
            Add Your Product
        </Button>
    );

const LearnMoreLink = (props: AnchorProps) => (
    <Anchor component={Link} href={MarketingHelpers.urls.resourcepro.joinAsPro()} {...props}>
        Learn more
    </Anchor>
);

const AddProductCard = () => {
    const action = useAddProductAction();

    return (
        <Card withBorder className={[cx.root, 'gradient-cyan-green-background-light'].join(' ')}>
            <Box className={cx.icon}>
                <BsBox />
            </Box>
            <Title order={3} size="h2">
                Your product here?
            </Title>
            <Text>Showcase your product to industry professionals.</Text>
            <AddProductButton action={action} />
            {action.explainsPro && <LearnMoreLink mt={-8} />}
        </Card>
    );
};

const AddProductCardHorizontal = () => {
    const action = useAddProductAction();

    return (
        <Card p={13} ta="left" withBorder className="gradient-cyan-green-background-light">
            <Flex gap="sm" wrap="nowrap" h="100%">
                <Box m={-10} mr={0} h={'calc(100% + 20px)'}>
                    <ThemeIcon radius={0} size={100} p={30} variant="light" h="100%" bg="rgba(0,0,0,0.05)" c="gray.8">
                        <BsBox size={30} />
                    </ThemeIcon>
                </Box>
                <Stack gap={6} align="flex-start">
                    <Text fw={600} fz={14}>
                        Your product here?
                    </Text>

                    <Text>Showcase your product to industry professionals.</Text>

                    <Group gap="sm" mt={2}>
                        <AddProductButton action={action} size="xs" />
                        {action.explainsPro && <LearnMoreLink fz={12} />}
                    </Group>
                </Stack>
            </Flex>
        </Card>
    );
};

export { AddProductCard, AddProductCardHorizontal };
